import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components';
import type { AppData, Transaction } from '../../data/types';
import { formatCurrency } from '../../lib/currency';

type Props = {
  data: AppData;
  today: Date;
};

const MONTHS = 6;
const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function txDate(t: Transaction): Date {
  const [y, m, d] = t.date.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/**
 * Last-6-months cash flow. Income vs expenses per calendar month with
 * net annotation above each pair.
 */
export function CashFlowChart({ data, today }: Props) {
  const series = useMemo(() => {
    const rows: { key: string; label: string; income: number; expense: number; net: number }[] = [];
    for (let i = MONTHS - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      rows.push({
        key: monthKey(d),
        label: SHORT_MONTH[d.getMonth()]!,
        income: 0,
        expense: 0,
        net: 0,
      });
    }
    const idx = new Map(rows.map((r, i) => [r.key, i]));
    for (const t of data.transactions) {
      const d = txDate(t);
      const k = monthKey(d);
      const i = idx.get(k);
      if (i === undefined) continue;
      if (t.type === 'income') rows[i]!.income += t.amount;
      else rows[i]!.expense += t.amount;
    }
    for (const r of rows) r.net = r.income - r.expense;
    return rows;
  }, [data, today]);

  const currency = data.profile.currency;

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <div className="mb-3">
        <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100">
          Cash flow
        </h2>
        <p className="text-[13px] font-medium text-zinc-500">Last 6 months</p>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 16, right: 4, left: -16, bottom: 0 }}>
            <CartesianGrid
              vertical={false}
              stroke="rgb(var(--text-tertiary) / 0.15)"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#8E8E93', fontSize: 11 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#8E8E93', fontSize: 11 }}
              tickFormatter={(v: number) => {
                const r = Math.round(v);
                if (Math.abs(r) < 1000) return `$${r}`;
                const k = r / 1000;
                const decimals = Math.abs(k) < 10 ? 1 : 0;
                return `$${k.toFixed(decimals)}k`;
              }}
              allowDecimals={false}
            />
            <Tooltip
              separator=": "
              formatter={(v) => formatCurrency(Number(v) || 0, { currency })}
              contentStyle={{
                background: 'rgb(var(--surface))',
                border: '1px solid rgb(var(--surface-secondary))',
                borderRadius: 12,
                fontSize: 12,
              }}
            />
            <Bar
              dataKey="income"
              fill="rgb(var(--positive))"
              radius={[6, 6, 0, 0]}
              animationDuration={650}
            />
            <Bar
              dataKey="expense"
              fill="rgb(var(--negative))"
              radius={[6, 6, 0, 0]}
              animationDuration={650}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 grid grid-cols-6 gap-1 text-center">
        {series.map((r) => (
          <div key={r.key} className="text-[10px] font-mono tabular-nums">
            <span
              className={
                r.net >= 0 ? 'text-positive' : 'text-negative'
              }
            >
              {r.net >= 0 ? '+' : ''}
              {formatCurrency(r.net, { currency })}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}
